import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate } from '../lib/format';
import { Button, useToast } from './ui';

/**
 * Funktionen einer Person beenden oder loeschen - ueberall gleich.
 *
 * Zwei Fälle, die oft verwechselt werden:
 * - **Beenden**: die Person hatte die Funktion wirklich. Sie bleibt im
 *   Verlauf stehen, mit Enddatum.
 * - **Löschen**: die Funktion war ein Fehlgriff und verschwindet ganz.
 *
 * Die Rechte entsprechen dem Server: Vereinsstruktur bearbeiten *und*
 * Zugriffsrechte verwalten. Sonst stuende hier eine Schaltfläche, die
 * anschliessend abgewiesen wird.
 */
export interface ManagedRole {
  id: string;
  roleName: string;
  isCurrent: boolean;
  validUntil?: string | null;
}

export function usePersonRoleActions(): {
  may: boolean;
  busy: boolean;
  end: (role: ManagedRole) => void;
  remove: (role: ManagedRole) => void;
} {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['persons'] }),
      queryClient.invalidateQueries({ queryKey: ['person'] }),
      queryClient.invalidateQueries({ queryKey: ['organisation'] }),
      queryClient.invalidateQueries({ queryKey: ['operation-accounts'] }),
    ]);
  };
  const fail = (fallback: string) => (error: unknown) =>
    toast.show(error instanceof ApiError ? error.message : fallback, 'error');

  const end = useMutation({
    mutationFn: (role: ManagedRole) => api.del(`/api/person-roles/${role.id}`),
    onSuccess: async () => { await refresh(); toast.show('Funktion beendet'); },
    onError: fail('Funktion konnte nicht beendet werden'),
  });
  const remove = useMutation({
    mutationFn: (role: ManagedRole) => api.del(`/api/person-roles/${role.id}?purge=true`),
    onSuccess: async () => { await refresh(); toast.show('Funktion gelöscht'); },
    onError: fail('Funktion konnte nicht gelöscht werden'),
  });

  return {
    may: can('organisation', 'update') && can('permissions', 'manage'),
    busy: end.isPending || remove.isPending,
    end: (role) => {
      if (window.confirm(`Funktion „${role.roleName}“ beenden? Sie bleibt mit Enddatum im Verlauf sichtbar.`)) {
        end.mutate(role);
      }
    },
    remove: (role) => {
      if (window.confirm(`Funktion „${role.roleName}“ endgültig löschen? Nur sinnvoll, wenn sie versehentlich vergeben wurde.`)) {
        remove.mutate(role);
      }
    },
  };
}

/** Beenden bzw. Löschen - je nachdem, ob die Funktion noch läuft. */
export function RoleActions({ role }: { role: ManagedRole }): JSX.Element | null {
  const actions = usePersonRoleActions();
  if (!actions.may) return null;
  return role.isCurrent ? (
    <Button size="sm" variant="ghost" disabled={actions.busy} onClick={() => actions.end(role)}>Beenden</Button>
  ) : (
    <Button size="sm" variant="ghost" disabled={actions.busy} onClick={() => actions.remove(role)}>Löschen</Button>
  );
}

/** Einheitlicher Zusatz an beendeten Funktionen. */
export function roleValidity(role: ManagedRole): string {
  return role.isCurrent ? '' : role.validUntil ? ` · beendet ${formatDate(role.validUntil)}` : ' · beendet';
}
